import { useResource } from '@landsbankinn/l16n';
import { Form } from '@landsbankinn/ui-kit';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useScreenSize } from 'src/hooks/useScreenSize';
import {
  ConsentServiceEvents,
  ConsentServiceToken,
  type IConsentService,
} from 'src/services/ConsentService';
import { ServicesProvider } from 'src/services/ServicesProvider';
import { useApplicationServerContext } from 'src/state';
import {
  isConsentServerState,
  type ConsentServerState,
  type Scope,
} from 'src/state/server';
import { LandsbankinnOnlineBank } from '../Layout/Desktop/LandsbankinnOnlineBank';
import { submit } from '../Login/utils';
import { Body } from './Body';
import { DataView } from './DataView';
import {
  ScopeError,
  useConsentClientContext,
} from './State/consentClientState';

export function Consent() {
  const { isMobile } = useScreenSize();
  const { locale } = useResource();
  const [consentClient, consentDispatch] = useConsentClientContext();
  const [serverState] = useApplicationServerContext() as [ConsentServerState];
  const formRef = useRef<HTMLFormElement>(null);
  const [action, setAction] = useState<string>('/ui/consent/reject');

  const consentService = useMemo(() => {
    if (isConsentServerState(serverState)) {
      const consentService = ServicesProvider.getSingleton<IConsentService>(
        ConsentServiceToken,
        serverState.scope.fullName,
      );

      consentService.start();
      return consentService;
    }
  }, [serverState]);

  useEffect(() => {
    const listenerIds: string[] = [];
    if (consentService) {
      listenerIds.push(
        consentService.on(
          ConsentServiceEvents.OnConsentError,
          (type: ScopeError) => {
            consentDispatch({ type: 'setError', payload: { error: type } });
          },
        ),
      );

      listenerIds.push(
        consentService.on(
          ConsentServiceEvents.ReceiveScopeAsync,
          (sc: Scope) => {
            if (sc) {
              const local = sc.localization
                ? sc.localization[locale]
                : undefined;
              sc.data.forEach((data) => {
                if (!data.isLocalized && local) {
                  data.localized_name = local[data.name] ?? data.name;
                  data.isLocalized = true;
                }
              });
              consentDispatch({
                type: 'setScopeAndLocalization',
                payload: { scope: sc, localization: local },
              });
            }
          },
        ),
      );

      listenerIds.push(
        consentService.on(ConsentServiceEvents.OnScopeUpdated, () => {
          consentDispatch({ type: 'next view' });
        }),
      );

      consentService.start();
    }

    return () => {
      if (consentService) {
        consentService.off(listenerIds);
      }
    };
  }, [consentDispatch, consentService, locale, serverState.scope.fullName]);

  useEffect(() => {
    if (consentClient.consent) {
      setAction('/ui/consent');
      // Make sure scope has updated before submitting
      if (!consentClient.updateScopeData) {
        if (formRef.current) submit(formRef.current);
      }
    }
    if (consentClient.reject) {
      setAction('/ui/consent/reject');
      if (serverState.landsbankiClient) {
        setAction('/ui/consent/back');
      }
      if (formRef.current) submit(formRef.current);
    }
  }, [
    consentClient.consent,
    consentClient.reject,
    consentClient.updateScopeData,
    serverState.landsbankiClient,
  ]);

  useEffect(() => {
    if (consentService && consentClient.updateScopeData) {
      consentService.updateScope(consentClient.values);
    }
  }, [consentClient.updateScopeData, consentClient.values, consentService]);

  return (
    <Form
      color="blue font"
      ref={formRef}
      method="post"
      action={action}
      onSubmit={() => {}}
    >
      <input
        type="hidden"
        name={serverState.form.fields.traceparent.name}
        value={serverState.form.fields.traceparent.value}
      />
      <input
        type="hidden"
        name={serverState.form.fields.xsrf.name}
        value={serverState.form.fields.xsrf.value}
      />
      {isMobile && (
        <Body>
          <DataView />
        </Body>
      )}
      {!isMobile && (
        <LandsbankinnOnlineBank>
          <Body>
            <DataView />
          </Body>
        </LandsbankinnOnlineBank>
      )}
    </Form>
  );
}
